const STATUS_LABELS = {
  pending: 'Bekliyor',
  'in-progress': 'Devam Ediyor',
  completed: 'Tamamlandı',
};

const PRIORITY_LABELS = {
  high: 'Yüksek',
  medium: 'Orta',
  low: 'Düşük',
};

export const formatTaskStatus = (status) => {
  if (!status) return 'Bilinmiyor';
  return STATUS_LABELS[status] || status;
};

export const formatTaskPriority = (priority) => {
  if (!priority) return 'Belirtilmemiş';
  return PRIORITY_LABELS[priority] || priority;
};

export const formatTaskDate = (dateString, fallback = 'Tarih yok') => {
  if (!dateString) return fallback;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString('tr-TR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatTaskDateTime = (dateString, fallback = 'Bilinmiyor') => {
  if (!dateString) return fallback;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString('tr-TR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const isTaskOverdue = (dateString, status) => {
  if (!dateString || status === 'completed') return false;
  const dueDate = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  dueDate.setHours(0, 0, 0, 0);
  return dueDate < today;
};

export const getStatusColor = (status) => {
  switch (status) {
    case 'completed': return 'bg-success';
    case 'in-progress': return 'bg-warning';
    case 'pending': return 'bg-error';
    default: return 'bg-info';
  }
};

export const getStatusBadge = (status) => {
  switch (status) {
    case 'completed': return 'badge-success';
    case 'in-progress': return 'badge-warning';
    case 'pending': return 'badge-error';
    default: return 'badge-info';
  }
};

export const getPriorityBadge = (priority) => {
  switch (priority) {
    case 'high': return 'badge-error';
    case 'medium': return 'badge-warning';
    case 'low': return 'badge-info';
    default: return 'badge-ghost';
  }
};

export const getTagColor = (index) => {
  const colors = [
    'badge-primary',
    'badge-secondary',
    'badge-accent',
    'badge-info',
    'badge-success',
    'badge-warning',
  ];
  return colors[index % colors.length];
};

export const createChecklistItem = (text = '') => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
  text,
  completed: false,
  children: [],
});

export const normalizeChecklistItem = (item = {}) => ({
  id: item.id ?? createChecklistItem().id,
  text: item.text ?? '',
  completed: !!item.completed,
  children: (item.children || []).map(normalizeChecklistItem),
});

export const normalizeChecklist = (items = []) => items.map(normalizeChecklistItem);

export const flattenChecklist = (items = []) => {
  const result = [];
  items.forEach((item) => {
    result.push(item);
    if (item.children?.length) {
      result.push(...flattenChecklist(item.children));
    }
  });
  return result;
};

export const getChecklistStats = (items = []) => {
  const flat = flattenChecklist(items);
  const completed = flat.filter((item) => item.completed).length;
  return { total: flat.length, completed };
};

export const cleanChecklist = (items = []) => items
  .map((item) => ({
    ...item,
    children: cleanChecklist(item.children || []),
  }))
  .filter((item) => item.text.trim() || item.children.length);

export const markChecklistCompleted = (items = [], completed = true) => items.map((item) => ({
  ...item,
  completed,
  children: markChecklistCompleted(item.children || [], completed),
}));

export const isTavsiyelerimChecklistItem = (item) => (
  String(item?.text || '').trim().toLowerCase() === 'tavsiyelerim'
);

/** Görev kapatılırken tavsiyelerim maddesi ve alt maddeleri completed yapılmaz. */
export const markChecklistCompletedOnTaskClose = (items = []) => items.map((item) => {
  if (isTavsiyelerimChecklistItem(item)) {
    return {
      ...item,
      completed: false,
      children: (item.children || []).map((child) => ({ ...child, completed: false })),
    };
  }
  return {
    ...item,
    completed: true,
    children: markChecklistCompleted(item.children || [], true),
  };
});

export const getCompletionPercentage = (task) => {
  if (!task?.checklist?.length) {
    return task?.completed ? 100 : 0;
  }
  const { total, completed } = getChecklistStats(task.checklist);
  if (!total) return task?.completed ? 100 : 0;
  return Math.round((completed / total) * 100);
};

export const truncateText = (text, maxLength) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

export const findChecklistItem = (items = [], itemId) => {
  const id = String(itemId);
  for (const item of items) {
    if (String(item.id) === id) return { item, parent: null, isChild: false };
    for (const child of item.children || []) {
      if (String(child.id) === id) return { item: child, parent: item, isChild: true };
    }
  }
  return null;
};

export const findChecklistParent = (items = [], childId) => {
  const found = findChecklistItem(items, childId);
  return found?.parent || null;
};

export const deepCopyChecklist = (items = []) => items.map((item) => ({
  ...createChecklistItem(item.text),
  completed: false,
  children: (item.children || []).map((child) => ({
    ...createChecklistItem(child.text),
    completed: false,
    children: [],
  })),
}));

export const toPublicTask = (task) => {
  if (!task) return null;
  const checklist = normalizeChecklist(task.checklist || []);
  const completion_percentage = getCompletionPercentage({ ...task, checklist });
  return {
    id: task.id,
    title: task.title,
    description: task.description,
    status: task.status,
    priority: task.priority,
    due_date: task.due_date,
    completed: !!task.completed,
    tags: task.tags || [],
    checklist,
    completion_percentage,
    is_overdue: isTaskOverdue(task.due_date, task.status),
    created_at: task.created_at || task.created,
    updated_at: task.updated_at || task.updated,
  };
};

export const maskTasksForPublic = (tasks = []) => tasks
  .filter((t) => t.visibility === 'public')
  .map(toPublicTask);

export const buildAISummary = (action, task, extra = '') => {
  const title = task?.title ? `'${task.title}'` : 'Görev';
  const summaries = {
    'tasks.create': `${title} oluşturuldu.`,
    'tasks.update': `${title} güncellendi.`,
    'tasks.delete': `${title} silindi.`,
    'tasks.complete': `${title} tamamlandı. Kontrol listesi maddeleri işaretlendi.`,
    'tasks.reopen': `${title} yeniden açıldı.`,
    'tasks.set_status': `${title} durumu güncellendi.`,
    'tasks.set_priority': `${title} önceliği güncellendi.`,
    'tasks.set_due_date': `${title} son tarihi güncellendi.`,
    'tasks.set_visibility': `${title} görünürlüğü güncellendi.`,
    'tasks.duplicate': `${title} kopyalandı.`,
    'tasks.checklist.add': `Kontrol listesine madde eklendi.`,
    'tasks.checklist.child.add': `Alt madde eklendi.`,
    'tasks.checklist.child.toggle': `Alt madde güncellendi.`,
    'tasks.checklist.toggle': `Kontrol listesi maddesi güncellendi.`,
    'tasks.checklist.complete_all': `Kontrol listesinin tamamı tamamlandı.`,
    'tasks.append_notes': `Not eklendi.`,
    'tasks.add_tags': `Etiketler eklendi.`,
    'tasks.bulk_complete': `${extra || 'Seçili görevler'} tamamlandı.`,
    'tasks.bulk_update': `Toplu güncelleme yapıldı.`,
    'tasks.bulk_delete': `Toplu silme yapıldı.`,
  };
  return summaries[action] || extra || 'İşlem tamamlandı.';
};

export const applyStatusSideEffects = (status, task = {}) => {
  const updates = { status };
  if (status === 'completed') {
    updates.completed = true;
    updates.checklist = markChecklistCompletedOnTaskClose(normalizeChecklist(task.checklist || []));
  } else if (status === 'pending' || status === 'in-progress') {
    updates.completed = false;
  }
  return updates;
};

export const paginateItems = (items, { page = 1, perPage = 20 } = {}) => {
  const safePerPage = Math.min(Math.max(perPage, 1), 50);
  const safePage = Math.max(page, 1);
  const totalItems = items.length;
  const totalPages = Math.ceil(totalItems / safePerPage) || 1;
  const start = (safePage - 1) * safePerPage;
  return {
    items: items.slice(start, start + safePerPage),
    pagination: { page: safePage, perPage: safePerPage, totalItems, totalPages },
  };
};

export const isTaskIncomplete = (task) => {
  if (!task) return false;
  return task.status !== 'completed' && !task.completed;
};

export const sortTasks = (items, sort = 'created_at', order = 'desc') => {
  const dir = order === 'asc' ? 1 : -1;
  return [...items].sort((a, b) => {
    const aVal = a[sort] ?? a.created ?? '';
    const bVal = b[sort] ?? b.created ?? '';
    if (aVal < bVal) return -1 * dir;
    if (aVal > bVal) return 1 * dir;
    return 0;
  });
};

export const filterTasksByQuery = (items, q) => {
  if (!q) return items;
  const lower = q.toLowerCase();
  return items.filter((task) => {
    const haystack = [
      task.title,
      task.description,
      task.notes,
      ...(task.tags || []),
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(lower);
  });
};

export const computeTaskStats = (tasks = []) => {
  const stats = {
    total: tasks.length,
    pending: 0,
    in_progress: 0,
    completed: 0,
    overdue: 0,
    by_priority: { low: 0, medium: 0, high: 0 },
  };
  tasks.forEach((task) => {
    if (task.status === 'pending') stats.pending += 1;
    if (task.status === 'in-progress') stats.in_progress += 1;
    if (task.status === 'completed') stats.completed += 1;
    if (task.priority && stats.by_priority[task.priority] !== undefined) {
      stats.by_priority[task.priority] += 1;
    }
    if (isTaskOverdue(task.due_date, task.status)) stats.overdue += 1;
  });
  return stats;
};

export const collectTags = (tasks = []) => {
  const tagSet = new Set();
  tasks.forEach((task) => (task.tags || []).forEach((tag) => tagSet.add(tag)));
  return [...tagSet].sort();
};
